import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme, useAuth } from '../App'
import { api, User } from '../services/api'

function base64urlToBuffer(b64url: string): ArrayBuffer {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/')
  const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4))
  const bin = atob(b64 + pad)
  const arr = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i)
  return arr.buffer
}

function bufferToBase64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let s = ''
  bytes.forEach(b => s += String.fromCharCode(b))
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

type Lang = 'zh-TW' | 'en' | 'ja'
const LANG_LABELS: Record<Lang, string> = { 'zh-TW': '繁體中文', 'en': 'English', 'ja': '日本語' }

export default function SettingsPage() {
  const navigate = useNavigate()
  const { isDarkMode, toggleDarkMode } = useTheme()
  const { user } = useAuth()

  const [blockedUsers, setBlockedUsers] = useState<User[]>([])
  const [isLoadingBlocked, setIsLoadingBlocked] = useState(false)
  const [showBlockedUsers, setShowBlockedUsers] = useState(false)
  const [showKeyExport, setShowKeyExport] = useState(false)
  const [showLanguage, setShowLanguage] = useState(false)
  const [showAbout, setShowAbout] = useState(false)
  const [showPasskeys, setShowPasskeys] = useState(false)
  const [passkeys, setPasskeys] = useState<{ id: string; credential_id: string; device_name: string; created_at: string; last_used_at: string | null }[]>([])
  const [isLoadingPasskeys, setIsLoadingPasskeys] = useState(false)
  const [isRegisteringPasskey, setIsRegisteringPasskey] = useState(false)
  const [passkeyError, setPasskeyError] = useState<string | null>(null)
  const [identityKeyInfo, setIdentityKeyInfo] = useState<{ publicKey: string } | null>(null)
  const [language, setLanguage] = useState<Lang>('zh-TW')

  const [notifications, setNotifications] = useState({ messages: true, calls: true, sound: true })
  const [privacy, setPrivacy] = useState({ showOnline: true, readReceipts: true })

  useEffect(() => {
    const sn = localStorage.getItem('lynkstar_notifications'); if (sn) setNotifications(JSON.parse(sn))
    const sp = localStorage.getItem('lynkstar_privacy'); if (sp) setPrivacy(JSON.parse(sp))
    const sl = localStorage.getItem('lynkstar_language') as Lang; if (sl) setLanguage(sl)
    const ik = localStorage.getItem('lynkstar_identity_key'); if (ik) try { setIdentityKeyInfo(JSON.parse(ik)) } catch {}
  }, [])

  const saveNotifications = (ns: typeof notifications) => { setNotifications(ns); localStorage.setItem('lynkstar_notifications', JSON.stringify(ns)) }
  const savePrivacy = (ps: typeof privacy) => { setPrivacy(ps); localStorage.setItem('lynkstar_privacy', JSON.stringify(ps)) }
  const changeLanguage = (l: Lang) => { setLanguage(l); localStorage.setItem('lynkstar_language', l); setShowLanguage(false) }

  const loadBlockedUsers = async () => { setIsLoadingBlocked(true); try { setBlockedUsers(await api.getBlockedUsers()) } catch {} finally { setIsLoadingBlocked(false) } }

  const loadPasskeys = useCallback(async () => {
    setIsLoadingPasskeys(true)
    setPasskeyError(null)
    try {
      const res = await api.listPasskeys()
      setPasskeys(res as any[] || [])
    } catch (e) {
      setPasskeyError(e instanceof Error ? e.message : 'Failed to load passkeys')
    } finally {
      setIsLoadingPasskeys(false)
    }
  }, [])

  const registerPasskey = async () => {
    if (!window.PublicKeyCredential) { setPasskeyError('此瀏覽器不支援 Passkey'); return }
    setIsRegisteringPasskey(true)
    setPasskeyError(null)
    try {
      const { options } = await api.getPasskeyRegisterOptions()
      const publicKeyOptions: CredentialCreationOptions = {
        publicKey: {
          rp: options.rp,
          user: { ...options.user, id: base64urlToBuffer(options.user.id as unknown as string) },
          challenge: base64urlToBuffer(options.challenge),
          pubKeyCredParams: options.pubKeyCredParams,
          timeout: options.timeout,
          authenticatorSelection: options.authenticatorSelection,
          attestation: options.attestation,
        },
      }
      const credential = await navigator.credentials.create(publicKeyOptions) as PublicKeyCredential | null
      if (!credential) { setPasskeyError('Passkey 註冊已取消'); return }
      const attestation = credential.response as AuthenticatorAttestationResponse
      await api.verifyPasskeyRegister({
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufferToBase64url(attestation.clientDataJSON),
          attestationObject: bufferToBase64url(attestation.attestationObject),
        },
      })
      await loadPasskeys()
    } catch (e) {
      if (e instanceof Error && e.name === 'NotAllowedError') {
        setPasskeyError('Passkey 註冊已取消或逾時')
      } else {
        setPasskeyError(e instanceof Error ? e.message : 'Passkey 註冊失敗')
      }
    } finally {
      setIsRegisteringPasskey(false)
    }
  }

  const deletePasskey = async (id: string) => {
    if (!confirm('確定要刪除此 Passkey？')) return
    try {
      await api.deletePasskey(id)
      setPasskeys(prev => prev.filter(p => p.id !== id))
    } catch (e) {
      setPasskeyError(e instanceof Error ? e.message : 'Failed to delete')
    }
  }

  const handleExportKeys = () => {
    if (identityKeyInfo) {
      const blob = new Blob([JSON.stringify(identityKeyInfo, null, 2)], { type: 'application/json' })
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `lynkstar-keys-${user?.username || 'backup'}.json`; a.click()
    }
  }

  const handleClearStorage = () => {
    if (!confirm('確定要清除所有媒體快取嗎？')) return
    // Only clear media cache, not keys
    const keysToKeep = ['lynkstar_token', 'lynkstar_user', 'lynkstar_identity_key', 'lynkstar_my_uuid', 'lynkstar_theme', 'lynkstar_notifications', 'lynkstar_privacy', 'lynkstar_language']
    const allKeys = Object.keys(localStorage).filter(k => k.startsWith('lynkstar_') && !keysToKeep.includes(k))
    allKeys.forEach(k => localStorage.removeItem(k))
    alert('已清除快取')
  }

  const Toggle = ({ on, onToggle }: { on: boolean; onToggle: () => void }) => (
    <button onClick={onToggle} className={`relative w-12 h-6 rounded-full transition-colors ${on ? 'bg-primary' : 'bg-gray-300'}`}>
      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-6' : 'translate-x-0.5'}`} />
    </button>
  )

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-dark-bg">
      <header className="app-bar">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/10 rounded-full"><svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg></button>
        <h1 className="text-lg font-semibold flex-1">設定</h1>
      </header>

      <main className="flex-1 overflow-y-auto">
        {/* Appearance */}
        <section className="p-4">
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">外觀</h2>
          <div className="bg-gray-50 dark:bg-dark-surface rounded-xl overflow-hidden divide-y divide-gray-200 dark:divide-gray-700">
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg><span className="text-gray-700 dark:text-white">深色模式</span></div>
              <Toggle on={isDarkMode} onToggle={toggleDarkMode} />
            </div>
            <button onClick={() => setShowLanguage(true)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700">
              <div className="flex items-center gap-3"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" /></svg><span className="text-gray-700 dark:text-white">語言</span></div>
              <span className="text-gray-400 text-sm">{LANG_LABELS[language]}</span>
            </button>
          </div>
        </section>

        {/* Notifications */}
        <section className="p-4">
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">通知</h2>
          <div className="bg-gray-50 dark:bg-dark-surface rounded-xl overflow-hidden divide-y divide-gray-200 dark:divide-gray-700">
            <div className="px-4 py-3 flex items-center justify-between"><span className="text-gray-700 dark:text-white">訊息通知</span><Toggle on={notifications.messages} onToggle={() => saveNotifications({ ...notifications, messages: !notifications.messages })} /></div>
            <div className="px-4 py-3 flex items-center justify-between"><span className="text-gray-700 dark:text-white">來電通知</span><Toggle on={notifications.calls} onToggle={() => saveNotifications({ ...notifications, calls: !notifications.calls })} /></div>
            <div className="px-4 py-3 flex items-center justify-between"><span className="text-gray-700 dark:text-white">聲音</span><Toggle on={notifications.sound} onToggle={() => saveNotifications({ ...notifications, sound: !notifications.sound })} /></div>
          </div>
        </section>

        {/* Privacy */}
        <section className="p-4">
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">隱私</h2>
          <div className="bg-gray-50 dark:bg-dark-surface rounded-xl overflow-hidden divide-y divide-gray-200 dark:divide-gray-700">
            <div className="px-4 py-3 flex items-center justify-between"><span className="text-gray-700 dark:text-white">顯示上線狀態</span><Toggle on={privacy.showOnline} onToggle={() => savePrivacy({ ...privacy, showOnline: !privacy.showOnline })} /></div>
            <div className="px-4 py-3 flex items-center justify-between"><span className="text-gray-700 dark:text-white">已讀回執</span><Toggle on={privacy.readReceipts} onToggle={() => savePrivacy({ ...privacy, readReceipts: !privacy.readReceipts })} /></div>
            <button onClick={() => { setShowBlockedUsers(true); loadBlockedUsers() }} className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700"><span className="text-gray-700 dark:text-white">已封鎖用戶</span><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg></button>
          </div>
        </section>

        {/* Security */}
        <section className="p-4">
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">安全性</h2>
          <div className="bg-gray-50 dark:bg-dark-surface rounded-xl overflow-hidden divide-y divide-gray-200 dark:divide-gray-700">
            <button onClick={() => { setShowPasskeys(true); loadPasskeys() }} className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700"><div className="flex items-center gap-3"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" /></svg><span className="text-gray-700 dark:text-white">Passkey 管理</span></div><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg></button>
            <button onClick={() => setShowKeyExport(true)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700"><div className="flex items-center gap-3"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg><span className="text-gray-700 dark:text-white">匯出加密金鑰</span></div><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg></button>
            <div className="px-4 py-3"><div className="flex items-center gap-3 mb-1"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-secure-green" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg><span className="text-gray-700 dark:text-white">Signal Protocol</span></div><p className="text-sm text-gray-500 dark:text-gray-400 ml-8">所有訊息皆受端對端加密保護</p></div>
          </div>
        </section>

        {/* Storage */}
        <section className="p-4">
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">儲存空間</h2>
          <div className="bg-gray-50 dark:bg-dark-surface rounded-xl overflow-hidden">
            <button onClick={handleClearStorage} className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700"><span className="text-gray-700 dark:text-white">清除媒體快取</span><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg></button>
          </div>
        </section>

        {/* About */}
        <section className="p-4 pb-8">
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">關於</h2>
          <div className="bg-gray-50 dark:bg-dark-surface rounded-xl overflow-hidden divide-y divide-gray-200 dark:divide-gray-700">
            <div className="px-4 py-3 flex items-center justify-between"><span className="text-gray-700 dark:text-white">版本</span><span className="text-gray-500">1.1.0</span></div>
            <button onClick={() => setShowAbout(true)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700"><span className="text-gray-700 dark:text-white">關於 LYNKSTAR</span><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg></button>
            <div className="px-4 py-3 text-center text-xs text-gray-400">© 2026 LYNKSTAR · MIT License</div>
          </div>
        </section>
      </main>

      {/* Modals */}
      {showBlockedUsers && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50" onClick={() => setShowBlockedUsers(false)}>
          <div className="bg-white dark:bg-dark-surface w-full max-w-lg rounded-t-2xl p-6 max-h-[70vh] overflow-y-auto animate-slide-up" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">已封鎖用戶</h2>
            {isLoadingBlocked ? <div className="flex justify-center h-32"><div className="spinner border-primary border-t-primary-light" /></div>
              : blockedUsers.length === 0 ? <p className="text-gray-500 text-center py-8">沒有封鎖的用戶</p>
              : blockedUsers.map(u => (
                <div key={u.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl mb-2">
                  <span className="text-gray-700 dark:text-white">{u.username}</span>
                  <button onClick={async () => { try { await api.unblockUser(u.id); setBlockedUsers(prev => prev.filter(x => x.id !== u.id)) } catch {} }} className="text-sm text-primary">解除封鎖</button>
                </div>
              ))}
          </div>
        </div>
      )}

      {showKeyExport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowKeyExport(false)}>
          <div className="bg-white dark:bg-dark-surface w-full max-w-sm rounded-2xl p-6 animate-fade-in" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">匯出金鑰</h2>
            <p className="text-gray-500 dark:text-gray-400 mb-6">金鑰將以 JSON 格式匯出。請妥善保管，擁有此金鑰的任何人都可以解密您的訊息。</p>
            <div className="flex gap-3">
              <button onClick={() => setShowKeyExport(false)} className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-700 rounded-xl text-gray-700 dark:text-white">取消</button>
              <button onClick={() => { handleExportKeys(); setShowKeyExport(false) }} className="flex-1 px-4 py-3 bg-primary rounded-xl text-white">匯出</button>
            </div>
          </div>
        </div>
      )}

      {showLanguage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowLanguage(false)}>
          <div className="bg-white dark:bg-dark-surface w-full max-w-sm rounded-2xl p-6 animate-fade-in" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">選擇語言</h2>
            {(Object.keys(LANG_LABELS) as Lang[]).map(l => (
              <button key={l} onClick={() => changeLanguage(l)} className={`w-full px-4 py-3 rounded-xl text-left mb-2 ${language === l ? 'bg-primary text-white' : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}`}>{LANG_LABELS[l]}</button>
            ))}
          </div>
        </div>
      )}

      {showPasskeys && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50" onClick={() => setShowPasskeys(false)}>
          <div className="bg-white dark:bg-dark-surface w-full max-w-lg rounded-t-2xl p-6 max-h-[70vh] overflow-y-auto animate-slide-up" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Passkey 管理</h2>
              <button onClick={registerPasskey} disabled={isRegisteringPasskey} className="px-4 py-2 bg-primary text-white rounded-xl text-sm disabled:opacity-50">
                {isRegisteringPasskey ? '註冊中...' : '＋ 新增 Passkey'}
              </button>
            </div>
            {passkeyError && <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/30 rounded-xl text-red-600 dark:text-red-400 text-sm">{passkeyError}</div>}
            {isLoadingPasskeys ? (
              <div className="flex justify-center h-32"><div className="spinner border-primary border-t-primary-light" /></div>
            ) : passkeys.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400 mb-2">尚未註冊任何 Passkey</p>
                <p className="text-gray-400 dark:text-gray-500 text-sm">新增 Passkey 可使用生物辨識快速登入</p>
              </div>
            ) : (
              passkeys.map(pk => (
                <div key={pk.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl mb-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" /></svg>
                      <span className="text-gray-700 dark:text-white font-medium">{pk.device_name || 'Passkey'}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1 ml-7">建立於 {new Date(pk.created_at).toLocaleDateString()}</p>
                    {pk.last_used_at && <p className="text-xs text-gray-400 ml-7">上次使用 {new Date(pk.last_used_at).toLocaleDateString()}</p>}
                  </div>
                  <button onClick={() => deletePasskey(pk.id)} className="text-sm text-red-500 hover:text-red-600 px-3 py-1">刪除</button>
                </div>
              ))
            )}
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-4 text-center">Passkey 使用裝置的生物辨識（Face ID / 指紋）進行安全登入，私鑰永遠不會離開您的裝置。</p>
          </div>
        </div>
      )}

      {showAbout && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAbout(false)}>
          <div className="bg-white dark:bg-dark-surface w-full max-w-sm rounded-2xl p-6 animate-fade-in text-center" onClick={e => e.stopPropagation()}>
            <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4"><span className="text-3xl text-white font-bold">L</span></div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">LYNKSTAR</h2>
            <p className="text-sm text-gray-500 mt-1">版本 1.1.0</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">安全、開源的端對端加密通訊應用</p>
            <p className="text-xs text-gray-400 mt-4">Powered by Signal Protocol</p>
            <p className="text-xs text-gray-400 mt-1">MIT License © 2026</p>
            <button onClick={() => setShowAbout(false)} className="btn-primary w-full mt-6">關閉</button>
          </div>
        </div>
      )}
    </div>
  )
}
